import { useState, useEffect, useCallback, type ReactNode } from "react";
import {
  AuthContext,
  type User,
  type AuthState,
  authLogin,
  authRegister,
  authLogout,
  authRefresh,
  authStatus,
  authForgotPassword,
  authResetPassword,
  authChangePassword,
  authLinkLicense,
  getRefreshToken,
} from "../lib/auth";

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const refreshAuth = useCallback(async (): Promise<boolean> => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return false;
    }

    try {
      const result = await authRefresh();
      if (result.success) {
        const statusResult = await authStatus();
        if (statusResult.authenticated && statusResult.user) {
          setState({
            user: statusResult.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          return true;
        }
      }
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      return false;
    } catch {
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      return false;
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const login = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const result = await authLogin(email, password);
      if (result.success && result.user) {
        setState({
          user: result.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        return { success: true, message: result.message };
      }
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.message,
      }));
      return { success: false, message: result.message };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      return { success: false, message };
    }
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const result = await authRegister(email, password);
      if (result.success && result.user) {
        setState({
          user: result.user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
        return { success: true, message: result.message };
      }
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: result.message,
      }));
      return { success: false, message: result.message };
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Registration failed";
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      return { success: false, message };
    }
  }, []);

  const logout = useCallback(async () => {
    await authLogout();
    setState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    try {
      return await authForgotPassword(email);
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : "Request failed",
      };
    }
  }, []);

  const resetPassword = useCallback(async (token: string, password: string) => {
    try {
      return await authResetPassword(token, password);
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : "Reset failed",
      };
    }
  }, []);

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      try {
        return await authChangePassword(currentPassword, newPassword);
      } catch (err) {
        return {
          success: false,
          message: err instanceof Error ? err.message : "Change failed",
        };
      }
    },
    []
  );

  const linkLicense = useCallback(async (licenseKey: string) => {
    try {
      const result = await authLinkLicense(licenseKey);
      if (result.success && result.license) {
        setState((prev) => ({
          ...prev,
          user: prev.user
            ? {
                ...prev.user,
                license: result.license as User["license"],
              }
            : null,
        }));
      }
      return { success: result.success, message: result.message };
    } catch (err) {
      return {
        success: false,
        message: err instanceof Error ? err.message : "Link failed",
      };
    }
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        refreshAuth,
        forgotPassword,
        resetPassword,
        changePassword,
        linkLicense,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
